import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export default function Home() {
  const canvasRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    
    const setCanvasSize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };

    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    const drawParametricSurface = (time) => {
      const width = canvas.width / dpr;
      const height = canvas.height / dpr;
      
      ctx.clearRect(0, 0, width, height);

      const gridSize = 60;
      const scale = 180;
      const centerX = width / 2;
      const centerY = height / 2 + 100;
      
      const angleX = 1.1 + Math.sin(time * 0.0003) * 0.15;
      const angleZ = time * 0.0002;

      const points = [];
      for (let i = 0; i < gridSize; i++) {
        points[i] = [];
        for (let j = 0; j < gridSize; j++) {
          const u = (i / (gridSize - 1)) * 2 - 1;
          const v = (j / (gridSize - 1)) * 2 - 1;
          
          const x = u * 2;
          const z = v * 2;
          const r = Math.sqrt(x * x + z * z);
          const y = Math.exp(-r * 0.8) * Math.cos(r * 4 - time * 0.001) * 0.8;
          
          const cosX = Math.cos(angleX);
          const sinX = Math.sin(angleX);
          const cosZ = Math.cos(angleZ);
          const sinZ = Math.sin(angleZ);
          
          let y1 = y * cosX - z * sinX;
          let z1 = y * sinX + z * cosX;
          
          let x2 = x * cosZ - y1 * sinZ;
          let y2 = x * sinZ + y1 * cosZ;
          
          const perspective = 4;
          const projScale = perspective / (perspective + z1);
          
          points[i][j] = {
            x: centerX + x2 * scale * projScale,
            y: centerY - y2 * scale * projScale,
            z: z1,
            brightness: y
          };
        }
      }

      ctx.strokeStyle = 'rgba(100, 180, 255, 0.4)';
      ctx.lineWidth = 0.8;

      for (let i = 0; i < gridSize; i++) {
        ctx.beginPath();
        for (let j = 0; j < gridSize; j++) {
          const point = points[i][j];
          if (j === 0) ctx.moveTo(point.x, point.y);
          else ctx.lineTo(point.x, point.y);
        }
        ctx.stroke();
      }

      for (let j = 0; j < gridSize; j++) {
        ctx.beginPath();
        for (let i = 0; i < gridSize; i++) {
          const point = points[i][j];
          if (i === 0) ctx.moveTo(point.x, point.y);
          else ctx.lineTo(point.x, point.y);
        }
        ctx.stroke();
      }

      const peakGradient = ctx.createRadialGradient(
        centerX, centerY - 50, 0,
        centerX, centerY - 50, 150
      );
      peakGradient.addColorStop(0, 'rgba(100, 180, 255, 0.3)');
      peakGradient.addColorStop(1, 'rgba(100, 180, 255, 0)');
      ctx.fillStyle = peakGradient;
      ctx.fillRect(0, 0, width, height);
    };

    let animationFrame;
    const animate = (time) => {
      drawParametricSurface(time);
      animationFrame = requestAnimationFrame(animate);
    };

    animate(0);

    return () => {
      window.removeEventListener('resize', setCanvasSize);
      cancelAnimationFrame(animationFrame);
    };
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    }}>
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
        }}
      />

      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(180deg, rgba(10,22,40,0) 0%, rgba(10,22,40,0.3) 100%)',
        zIndex: 1,
        pointerEvents: 'none',
      }} />

      <div style={{
        position: 'relative',
        zIndex: 2,
        padding: '0 8%',
        paddingTop: '100px',
        maxWidth: '1400px',
        margin: '0 auto',
        width: '100%',
      }}>
        <h1 style={{
          fontSize: 'clamp(2.5rem, 8vw, 7rem)',
          fontWeight: 800,
          letterSpacing: '-0.02em',
          margin: 0,
          color: '#ffffff',
          lineHeight: 1,
          textTransform: 'uppercase',
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 1s ease-out, transform 1s ease-out',
        }}>
          PARAMETRIX CAPITAL
        </h1>

        <p style={{
          fontSize: 'clamp(1rem, 2vw, 1.5rem)',
          fontWeight: 300,
          letterSpacing: '0.05em',
          margin: '1.5rem 0 0 0.25rem',
          color: '#a8c5e6',
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 1s ease-out 0.2s, transform 1s ease-out 0.2s',
        }}>
          Quantitative Investment Research
        </p>

        <div style={{
          width: '120px',
          height: '2px',
          background: 'linear-gradient(90deg, #4a9eff 0%, rgba(74, 158, 255, 0) 100%)',
          margin: '3rem 0',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 1s ease-out 0.4s',
        }} />

        <p style={{
          fontSize: 'clamp(0.95rem, 1.5vw, 1.15rem)',
          fontWeight: 300,
          lineHeight: 1.7,
          color: '#b8d4f1',
          maxWidth: '800px',
          margin: 0,
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 1s ease-out 0.6s, transform 1s ease-out 0.6s',
        }}>
          Parametrix Capital is a research firm applying scientific, parametric, 
          and probabilistic methods to design investment strategies and asset allocation. 
          Our research-driven framework emphasizes transparency, risk discipline, and 
          repeatability — converting data into structured conviction. We serve investors 
          seeking a precise, evidence-based approach to long-term capital stewardship.
        </p>

        <div style={{
          marginTop: '3rem',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 1s ease-out 0.8s',
        }}>
          <Link
            to="/service"
            style={{
              display: 'inline-block',
              padding: '1rem 2.5rem',
              background: 'transparent',
              border: '1px solid #4a9eff',
              color: '#4a9eff',
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: 500,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              borderRadius: '4px',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={e => {
              e.target.style.background = '#4a9eff';
              e.target.style.color = '#0a1628';
            }}
            onMouseLeave={e => {
              e.target.style.background = 'transparent';
              e.target.style.color = '#4a9eff';
            }}
          >
            Our Service
          </Link>
        </div>
      </div>
    </div>
  );
}
